import { Signal, useComputed } from "@suse-o11y/signal";
import React from "react";

import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { IconDelete } from "~/components/icon";
import { Metric } from "~/models/metric";
import { usePropagateFocusWhenInvalid } from "~/tools/impulse-form/use-propagate-focus-when-invalid";
import { prop } from "~/tools/prop";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";
import { uniqueBy } from "~/tools/unique-by";
import { useDelayed } from "~/tools/use-delayed";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { type RangePromQLResult, queryRangePromQL } from "../../../queries/metrics/batch";
import { useQuery } from "../../../queries/react-query";
import { Widget, WidgetBody, WidgetHeader } from "../../_widget";
import { HelpPopupTooltip } from "../../react/tooltip/HelpPopupTooltip";
import { WidgetQueryAlias, WidgetQueryMinStep, WidgetQueryPromql } from "../../widget-form/query";
import type { FormState } from "../../widget-form/query/form-state";
import { DEBOUNCE_QUERIES_MS } from "../_common";

function selectAllLabelNames(result: RangePromQLResult) {
	return result.transform((matrixes) => Metric.extractAllLabelNames(matrixes.map(prop("metric")))).getOrElse([]);
}

export interface QueryProps {
	timeRange: TimeRange;
	name: string;
	query: FormState;
	onDelete?: VoidFunction;
}

export const Query: React.FC<QueryProps> = ({ timeRange, query, name, onDelete }) => {
	const [expanded, setExpanded] = React.useState(true);
	// TODO do not convert to Signal https://stackstate.atlassian.net/browse/OBSFE-171
	// TODO it should use the useState for proper flushSync call inside the usePropagateFocusWhenInvalid hook
	const isExpanded = React.useMemo(() => Signal<boolean>(() => expanded, setExpanded), [expanded]);

	const variables = useTemplateVariables();

	const promql = useComputed(
		(monitor) => query.fields.promql.getOutput(monitor)?.interpolate(variables.values)?.trim() ?? "",
		[query, variables],
	);
	const debouncedPromQL = useDelayed(promql, DEBOUNCE_QUERIES_MS);

	/**
	 * Fetch the range query with the step equal to the full range,
	 * so it returns only few points for the labels extraction.
	 *
	 * I can't use the instant query here, because the labels can be different.
	 */
	const { data: metricLabels = [] } = useQuery({
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,

		enabled: debouncedPromQL.length > 0,
		select: selectAllLabelNames,

		...queryRangePromQL({
			query: debouncedPromQL,
			timeRange,
			step: timeRange.duration(),
		}),
	});

	const aliasVariablesSuggestions = React.useMemo(() => {
		return uniqueBy(
			[
				// metric labels override variables suggestions if duplicated
				...metricLabels.map((label) => ({ name: label, detail: "label" })),
				// variables have namespaced versions so they are still useful
				...variables.suggestions,
			],
			prop("name"),
		);
	}, [metricLabels, variables]);

	usePropagateFocusWhenInvalid(query, () => setExpanded(true));

	return (
		<FormField card className="items-stretch" el={Widget} isExpanded={isExpanded} state={query}>
			<WidgetHeader title={<span className="uppercase">{name}</span>}>
				<div className="flex flex-row justify-center gap-4">
					<FormFieldReset />

					{onDelete != null && (
						<button className="button button-sm button-tertiary button-square" onClick={onDelete} type="button">
							<IconDelete className="size-3" />
						</button>
					)}
				</div>
			</WidgetHeader>

			<WidgetBody className="space-y-4">
				<FormField state={query.fields.promql}>
					<FormLabel>PromQL Expression *</FormLabel>

					{/* configure scroll margin so the input is not hidden behind the sticky tabs */}
					<WidgetQueryPromql className="w-full scroll-mt-20" promql={query.fields.promql} />
				</FormField>

				<div className="flex flex-row gap-4">
					<FormField className="flex-1" state={query.fields.alias}>
						<FormLabel>Alias</FormLabel>

						<WidgetQueryAlias alias={query.fields.alias} suggestions={aliasVariablesSuggestions} />
					</FormField>

					<FormField className="w-1/4 min-w-32" state={query.fields.minStep}>
						<FormLabel>
							Min Step
							<HelpPopupTooltip
								className="ml-1 inline-flex h-5 translate-y-0.5"
								content="Minimum step interval (e.g. 8h30m)"
							/>
						</FormLabel>

						<WidgetQueryMinStep minStep={query.fields.minStep} suggestions={variables.suggestions} />
					</FormField>
				</div>
			</WidgetBody>
		</FormField>
	);
};
